import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Panel } from "@/components/dash";
import { inputCls, btnPrimary, btnGhost } from "@/components/admin/AdminLayout";

const MENUS = [["main", "Main menu"], ["company", "Company dropdown"], ["legal", "Footer · legal"], ["hidden", "Hidden / special"]];

export default function AdminPages() {
  const qc = useQueryClient();
  const { data: pages = [], refetch } = useQuery({ queryKey: ["admin-pages"], queryFn: async () => (await api.get("/admin/pages")).data });
  const [slug, setSlug] = useState("tech-transfer");
  const [f, setF] = useState(null);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.get(`/pages/${slug}`).then((r) => setF(r.data)).catch(() => setF({ slug, title: "", subtitle: "", html: "", menu: "main", order: 50, published: true }));
  }, [slug]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : k === "order" ? Number(e.target.value) : e.target.value });
  const save = async () => {
    try {
      await api.put(`/admin/pages/${slug}`, { title: f.title, subtitle: f.subtitle, html: f.html, menu: f.menu, order: f.order, published: f.published });
      toast.success("Page published"); refetch(); qc.invalidateQueries({ queryKey: ["pages"] }); qc.invalidateQueries({ queryKey: ["page", slug] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const importDocx = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const r = (await api.post("/admin/docx-to-html", fd)).data; setF({ ...f, html: r.html }); toast.success("Word content imported — review and publish"); } catch (e) { toast.error(errMsg(e)); }
  };
  const newPage = () => { const s = prompt("URL slug for new page (e.g. careers)"); if (s) setSlug(s.toLowerCase().replace(/[^a-z0-9-]/g, "-")); };
  const del = async () => { if (window.confirm(`Delete page ${slug}?`)) { await api.delete(`/admin/pages/${slug}`); refetch(); setSlug("about-us"); } };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="CMS · Website" title="Website Pages" desc="Edit menu pages, Tech Transfer, Plant Advisory, Terms of Use, Copyright and Disclaimer. Import content directly from a Word (.docx) file.">
        <button className={btnPrimary} onClick={newPage} data-testid="pages-new">+ New page</button>
      </PageHeader>
      <div className="grid lg:grid-cols-[240px_1fr] gap-6">
        <Panel title="Pages">
          {pages.map((p) => (
            <button key={p.slug} onClick={() => setSlug(p.slug)} data-testid={`pages-item-${p.slug}`}
              className={`w-full text-left px-3 py-2 rounded-md text-sm ${slug === p.slug ? "bg-[#1074BA]/20 text-white" : "text-slate-400 hover:text-white"}`}>
              {p.title}<span className="block text-[10px] font-mono text-slate-500">/{p.slug} · {p.menu}</span>
            </button>
          ))}
        </Panel>
        {f && (
          <Panel title={`/${slug}`} right={<div className="flex gap-2"><label className={`${btnGhost} flex items-center cursor-pointer`}>Import Word<input type="file" accept=".docx" className="hidden" data-testid="pages-import-docx" onChange={(e) => e.target.files[0] && importDocx(e.target.files[0])} /></label>
            <button className={btnGhost} onClick={() => setPreview(!preview)} data-testid="pages-preview-toggle">{preview ? "Edit HTML" : "Preview"}</button>{slug !== "home" && <button className={btnGhost} onClick={del}>Delete</button>}</div>}>
            <div className="grid gap-3">
              <input className={inputCls} placeholder="Title" value={f.title} onChange={set("title")} data-testid="pages-title" />
              <input className={inputCls} placeholder="Subtitle" value={f.subtitle} onChange={set("subtitle")} data-testid="pages-subtitle" />
              <div className="flex flex-wrap gap-3 items-center text-sm">
                <select className={inputCls} value={f.menu} onChange={set("menu")} data-testid="pages-menu">{MENUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                <label className="text-xs text-slate-400">Order<input type="number" className={`${inputCls} w-20 ml-2`} value={f.order} onChange={set("order")} /></label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={f.published} onChange={set("published")} /> Published</label>
              </div>
              {preview ? <div className="cms-prose border border-slate-800 rounded-md p-6 max-h-[60vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: f.html }} />
                : <textarea className={`${inputCls} h-[55vh] py-3 font-mono text-xs`} value={f.html} onChange={set("html")} data-testid="pages-html" />}
              <button className={btnPrimary} onClick={save} data-testid="pages-save">Publish</button>
            </div>
          </Panel>
        )}
      </div>
    </div>
  );
}
